import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, DollarSign, Clock } from 'lucide-react';
import { sampleRecentWins } from '@/data/sampleData';

interface RecentWin {
  id: string;
  userName: string;
  tipTitle: string;
  odds: number;
  profit: number;
  timestamp: string;
}

export function RecentWinsTicker() {
  const [wins, setWins] = useState<RecentWin[]>(sampleRecentWins);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % wins.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [wins.length]);

  const getTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const currentWin = wins[currentIndex];

  return (
    <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20 p-4 overflow-hidden min-h-[108px]">
      <div className="flex items-center gap-3 animate-fade-in min-h-[40px]">
        <div className="flex-shrink-0 w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-green-500" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-sm truncate">{currentWin.userName}</span>
            <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 border-green-500/30">
              WON
            </Badge>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="truncate">{currentWin.tipTitle}</span>
            <span>•</span>
            <span className="font-medium text-primary">{currentWin.odds}x</span>
          </div>
        </div>
        
        <div className="flex-shrink-0 text-right">
          <div className="flex items-center gap-1 text-green-600 font-bold">
            <DollarSign className="w-4 h-4" />
            <span>{currentWin.profit}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{getTimeAgo(currentWin.timestamp)}</span>
          </div>
        </div>
      </div>
      
      {/* Pagination dots */}
      <div className="flex justify-center gap-1 mt-3">
        {wins.map((_, index) => (
          <div
            key={index}
            className={`h-1 rounded-full transition-all ${
              index === currentIndex ? 'w-4 bg-green-500' : 'w-1 bg-muted'
            }`}
          />
        ))}
      </div>
    </Card>
  );
}
